import { useMemo, useCallback } from "react";
import {
  useListTrustedSenders,
  useCreateTrustedSender,
  getListTrustedSendersQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

function extractDomain(email: string): string | null {
  const at = email.lastIndexOf("@");
  if (at < 0) return null;
  const domain = email.slice(at + 1).trim().toLowerCase();
  return domain.length > 0 ? domain : null;
}

function domainMatchesTrusted(
  senderDomain: string,
  trustedDomain: string,
): boolean {
  return (
    senderDomain === trustedDomain ||
    senderDomain.endsWith(`.${trustedDomain}`)
  );
}

interface UseTrustedSenderCheckResult {
  isTrusted: boolean;
  isLoading: boolean;
  trustSender: () => void;
  isTrusting: boolean;
}

export function useTrustedSenderCheck(
  fromAddress: string | null | undefined,
  verificationStatus: string | null | undefined,
): UseTrustedSenderCheckResult {
  const queryClient = useQueryClient();
  const listQuery = useListTrustedSenders();
  const createMutation = useCreateTrustedSender();

  const senderDomain = useMemo(
    () => (fromAddress ? extractDomain(fromAddress) : null),
    [fromAddress],
  );

  const isTrusted = useMemo(() => {
    if (verificationStatus !== "fail") return false;
    if (!senderDomain) return false;
    const items = listQuery.data?.items;
    if (!items || items.length === 0) return false;
    return items.some((t) => domainMatchesTrusted(senderDomain, t.domain));
  }, [verificationStatus, senderDomain, listQuery.data?.items]);

  const trustSender = useCallback(() => {
    if (!senderDomain) return;
    createMutation.mutate(
      { data: { domain: senderDomain } },
      {
        onSuccess: () => {
          toast.success(`${senderDomain} added to trusted senders`);
          queryClient.invalidateQueries({
            queryKey: getListTrustedSendersQueryKey(),
          });
        },
        onError: (err) => {
          toast.error(apiErrorMessage(err, "Couldn't add trusted sender"));
        },
      },
    );
  }, [senderDomain, createMutation, queryClient]);

  return {
    isTrusted,
    isLoading: listQuery.isLoading,
    trustSender,
    isTrusting: createMutation.isPending,
  };
}
